import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, Users, CheckCircle2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

interface ActiveSession {
  id: string;
  summary: string | null;
  decision_type: string | null;
  phase: number;
  subphase: string;
}

interface AssignToSessionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  userId: string;
  userName: string;
  onComplete: (assignedCount?: number) => void;
}

const phaseLabel = (phase: number, subphase: string) => {
  if (subphase === '3.4') return 'Abgeschlossen';
  if (phase === 1) return 'Phase 1';
  if (phase === 2) return 'Phase 2';
  if (phase === 3) return 'Phase 3';
  return `Phase ${phase}`;
};

const phaseBadgeColor = (phase: number, subphase: string) => {
  if (subphase === '3.4') return 'bg-muted text-muted-foreground';
  if (phase === 1) return 'bg-primary/10 text-primary border-primary/20';
  if (phase === 2) return 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20';
  return 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20';
};

export function AssignToSessionDialog({
  open,
  onOpenChange,
  workspaceId,
  userId,
  userName,
  onComplete,
}: AssignToSessionDialogProps) {
  const { user } = useAuth();
  const [sessions, setSessions] = useState<ActiveSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedSessions, setSelectedSessions] = useState<Set<string>>(new Set());
  const [role, setRole] = useState<'contributor' | 'reviewer' | 'viewer'>('contributor');
  const [successState, setSuccessState] = useState<{ count: number } | null>(null);

  useEffect(() => {
    if (!open) return;
    setSelectedSessions(new Set());
    setRole('contributor');
    setSuccessState(null);
    loadSessions();
  }, [open, workspaceId]);

  const loadSessions = async () => {
    setLoading(true);
    try {
      // Get sessions in this workspace that are not completed (subphase != '3.4')
      const { data, error } = await supabase
        .from('decision_sessions')
        .select('id, summary, decision_type, phase, subphase')
        .eq('workspace_id', workspaceId)
        .neq('subphase', '3.4')
        .order('updated_at', { ascending: false });

      if (error) throw error;

      // Filter out sessions where user is already a participant
      const { data: existingParticipations } = await supabase
        .from('decision_participants')
        .select('session_id')
        .eq('user_id', userId);

      const existingSessionIds = new Set(
        (existingParticipations || []).map((p) => p.session_id)
      );

      setSessions(
        (data || []).filter((s) => !existingSessionIds.has(s.id))
      );
    } catch (err) {
      console.error('Failed to load sessions:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleSession = (sessionId: string) => {
    setSelectedSessions((prev) => {
      const next = new Set(prev);
      if (next.has(sessionId)) {
        next.delete(sessionId);
      } else {
        next.add(sessionId);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedSessions.size === sessions.length) {
      setSelectedSessions(new Set());
    } else {
      setSelectedSessions(new Set(sessions.map((s) => s.id)));
    }
  };

  const handleSubmit = async () => {
    if (!user || selectedSessions.size === 0) return;
    setSubmitting(true);
    const count = selectedSessions.size;
    try {
      const inserts = Array.from(selectedSessions).map((sessionId) => ({
        session_id: sessionId,
        user_id: userId,
        role,
        added_by: user.id,
      }));

      const { error } = await supabase
        .from('decision_participants')
        .insert(inserts);

      if (error) throw error;

      // Show success state briefly
      setSuccessState({ count });
      onComplete(count);

      setTimeout(() => {
        onOpenChange(false);
      }, 1200);
    } catch (err) {
      console.error('Failed to assign to sessions:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        {successState ? (
          <div className="flex flex-col items-center justify-center py-8 gap-3">
            <CheckCircle2 className="h-12 w-12 text-emerald-500 dark:text-emerald-400" />
            <p className="text-lg font-semibold text-foreground">Zugewiesen!</p>
            <p className="text-sm text-muted-foreground text-center">
              <strong>{userName}</strong> wurde zu {successState.count} Entscheidung{successState.count > 1 ? 'en' : ''} hinzugefügt.
            </p>
          </div>
        ) : (
        <>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            Zu Entscheidungen hinzufügen
          </DialogTitle>
          <DialogDescription>
            Wähle die Entscheidungen, zu denen <strong>{userName}</strong> Zugang erhalten soll.
          </DialogDescription>
        </DialogHeader>

        {/* Role selector */}
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">Rolle für alle gewählten Entscheidungen</label>
          <Select value={role} onValueChange={(v) => setRole(v as typeof role)}>
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="contributor">Contributor – Kann bearbeiten</SelectItem>
              <SelectItem value="reviewer">Reviewer – Kann kommentieren</SelectItem>
              <SelectItem value="viewer">Viewer – Nur lesen</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Session list */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-foreground">Entscheidungen</span>
            {sessions.length > 0 && (
              <Button variant="ghost" size="sm" className="text-xs h-7" onClick={toggleAll}>
                {selectedSessions.size === sessions.length ? 'Keine auswählen' : 'Alle auswählen'}
              </Button>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : sessions.length === 0 ? (
            <div className="py-6 text-center">
              <CheckCircle2 className="h-8 w-8 mx-auto text-muted-foreground/50 mb-2" />
              <p className="text-sm text-muted-foreground">
                Keine offenen Entscheidungen verfügbar oder der Nutzer ist bereits überall Teilnehmer.
              </p>
            </div>
          ) : (
            <ScrollArea className="max-h-[240px]">
              <div className="space-y-1.5">
                {sessions.map((session) => (
                  <label
                    key={session.id}
                    className="flex items-center gap-3 p-3 rounded-lg border border-border/50 bg-card hover:bg-muted/30 transition-colors cursor-pointer"
                  >
                    <Checkbox
                      checked={selectedSessions.has(session.id)}
                      onCheckedChange={() => toggleSession(session.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate text-foreground">
                        {session.summary || session.decision_type || 'Unbenannte Entscheidung'}
                      </p>
                    </div>
                    <Badge variant="outline" className={`text-[10px] flex-shrink-0 ${phaseBadgeColor(session.phase, session.subphase)}`}>
                      {phaseLabel(session.phase, session.subphase)}
                    </Badge>
                  </label>
                ))}
              </div>
            </ScrollArea>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
            Abbrechen
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={selectedSessions.size === 0 || submitting}
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
            ) : (
              <Users className="h-4 w-4 mr-1.5" />
            )}
            {selectedSessions.size > 0
              ? `Zuweisen & Anfrage abschließen (${selectedSessions.size})`
              : 'Entscheidungen wählen'}
          </Button>
        </DialogFooter>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
}
